import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import InvestmentClient from "../clients/InvestmentClient";
import { investmentKeys } from "./queryKeys";
import { type ApiClientClass, useApi } from "./useApi";

/**
 * onMutateInvestment will update the cache within react-query for investment,
 * this will prevent the need to constantly call refetchInvestment on components
 * when values are already available in memory from an update.
 *
 * @param client
 * @param updatePayload
 * @param investmentId
 */
export const onMutateInvestment = async (client: QueryClient, updatePayload: object, investmentId: string) => {
  await client.cancelQueries(investmentKeys.detail(investmentId));
  const previousInvestment: object | undefined = client.getQueryData(["investment", investmentId]);
  const newInvestment = {
    ...previousInvestment,
    ...updatePayload,
  };

  client.setQueryData(investmentKeys.detail(investmentId), newInvestment);

  return { previousInvestment, newInvestment };
};

/**
 *
 * @param client
 * @param investmentId
 * @param error
 * @param newData - The new investment record data after it has been updated
 * @param context
 */
export const onErrorInvestment = async (
  client: QueryClient,
  investmentId: string,
  error: unknown,
  newData: object,
  context?: Awaited<ReturnType<typeof onMutateInvestment>>,
) => {
  client.setQueryData(investmentKeys.detail(investmentId), context?.previousInvestment);
};

export const useCreateInvestment = (options = {}) => {
  const investmentApi = useApi(InvestmentClient as unknown as ApiClientClass<InvestmentClient>);
  return useMutation(
    (data: { offering_id: string; bcode?: string; vault_id?: string; saved_profile_id?: string; ref?: string }) =>
      investmentApi.create(data.offering_id, data.bcode, data.vault_id, data.saved_profile_id, data.ref),
    options,
  );
};

export const useCancelInvestment = (options = {}) => {
  const investmentApi = useApi(InvestmentClient as unknown as ApiClientClass<InvestmentClient>);
  const queryClient = useQueryClient();

  return useMutation((data: { id: string; reason?: string }) => investmentApi.cancel(data.id, data.reason), {
    ...options,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(investmentKeys.detail(variables.id));
    },
  });
};

export const useGetInvestment = (investmentId: string, options = {}) => {
  const investmentApi = useApi(InvestmentClient as unknown as ApiClientClass<InvestmentClient>);

  return useQuery(investmentKeys.vault(investmentId), () => investmentApi.getInvestmentById(investmentId), {
    ...options,
  });
};

export const useSubmitSubscription = (investmentId: string, options = {}) => {
  const client = useQueryClient();
  const investmentApi = useApi(InvestmentClient as unknown as ApiClientClass<InvestmentClient>);

  return useMutation((data: Record<string, unknown>) => investmentApi.submitSubscription(investmentId, data), {
    onMutate: async (payload) => await onMutateInvestment(client, payload, investmentId),
    onError: async (err, newData, context) => await onErrorInvestment(client, investmentId, err, newData, context),
    ...options,
  });
};

export const useUpdateStatusNext = (id: string, options = {}) => {
  const investmentApi = useApi(InvestmentClient as unknown as ApiClientClass<InvestmentClient>);
  return useMutation((data: object) => investmentApi.updateStatusNext(id, data), options);
};

export const useUpdateInvestment = (id: string, options = {}) => {
  const investmentApi = useApi(InvestmentClient as unknown as ApiClientClass<InvestmentClient>);
  return useMutation((data: object) => investmentApi.update(id, data), options);
};

export const useDispatchInvestmentEvent = (investmentId: string, options = {}) => {
  const investmentApi = useApi(InvestmentClient as unknown as ApiClientClass<InvestmentClient>);
  return useMutation((data: object) => investmentApi.dispatchEvent(investmentId, data), options);
};

export const useInvestmentHistory = (investmentId?: string, options = {}) => {
  const investmentApi = useApi(InvestmentClient as unknown as ApiClientClass<InvestmentClient>);

  return useQuery(investmentKeys.history(investmentId), () => investmentApi.history(investmentId), {
    enabled: !!investmentId,
    ...options,
  });
};
